package com.essntl.features.dashboard.ui

import com.essntl.core.utils.utils.loadingmanager.LoadingManager
import com.essntl.core.utils.utils.update
import com.essntl.core.utils.viewmodel.*
import com.essntl.features.authentication.domain.repository.AuthRepository
import com.essntl.features.category.domain.repository.CategoryRepository
import com.essntl.features.itinerary.domain.model.ItineraryFilterOptions
import com.essntl.features.itinerary.domain.model.ItineraryOrderType
import com.essntl.features.itinerary.domain.repository.ItineraryRepository
import com.essntl.features.itinerary.domain.use_case.UpdateItineraryServiceUseCase
import com.essntl.features.profile.domain.repository.ProfileRepository
import com.essntl.features.proposal.domain.model.ProposalFilterOptions
import com.essntl.features.proposal.domain.model.ProposalOrderType
import com.essntl.features.proposal.domain.repository.ProposalRepository
import com.essntl.features.task.domain.model.TaskFilterOptions
import com.essntl.features.task.domain.model.TaskModel
import com.essntl.features.task.domain.repository.TaskRepository
import com.essntl.features.task.domain.use_case.UpdateTaskUseCase
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

class DashboardViewModel(
    private val authRepository: AuthRepository,
    private val proposalRepository: ProposalRepository,
    private val taskRepository: TaskRepository,
    private val updateTaskUseCase: UpdateTaskUseCase,
    private val itineraryRepository: ItineraryRepository,
    private val categoryRepository: CategoryRepository,
    private val profileRepository: ProfileRepository,
    private val state: MutableStateFlow<DashboardState>,
    override val scope: CoroutineScope,
    override val loadingManager: LoadingManager,
) : ViewModel {

    init {
        getAllProfile()

        collectPaginationSuccessEvent(
            flow = updateTaskUseCase.successEvent,
            predicate = { first, second -> first.id == second.id },
            getState = { state.value.getAllTask },
            setState = { s ->
                state.update { state ->
                    state.copy(
                        getAllTask = state.getAllTask.copy(
                            data = s.data.filter { it.status == TaskModel.Status.Todo },
                        ),
                    )
                }
            },
        )
    }

    fun onEvent(event: DashboardEvent) {
        when (event) {
            is DashboardEvent.LoadItems -> loadItems()
            is DashboardEvent.SwitchProposals -> switchProposals()

            // Itinerary
            is DashboardEvent.SwitchItineraries -> switchItineraries()

            // Task
            is DashboardEvent.UpdateTask -> updateTask(event)
            is DashboardEvent.UpdateTaskStatus -> updateTaskStatus(event)
            is DashboardEvent.LoadTasks -> getNextTaskPage()
        }
    }

    private fun loadItems() =
        scope.launch {
            authRepository
                .getCurrentUserId()
                .onSuccess { userId ->
                    userId?.let { id ->
                        state.update { state ->
                            state.copy(
                                getCurrentUserId = id,
                                filterOptions = state.filterOptions.copy(selectedProfileIds = setOf(id)),
                            )
                        }
                    }
                }

            getAllProposal()
            getAllItinerary()
            getNextTaskPage()
        }

    private fun getAllProposal() {
        if (state.value.assigned) {
            val userId = state.value.getCurrentUserId
            if (userId.isNullOrEmpty())
                return

            getData(
                getData = {
                    proposalRepository.getAll(
                        page = 1L,
                        limit = 10,
                        filterOptions = ProposalFilterOptions(
                            selectedProfileIds = setOf(userId),
                            selectedProposalOrderType = setOf(ProposalOrderType.LastCreated),
                        ),
                    )
                },
                getState = { state.value.getAllProposal },
                setState = { newState -> state.update { it.copy(getAllProposal = newState) } },
            )
        } else
            getData(
                getData = { proposalRepository.getLastOpenedProposals() },
                getState = { state.value.getAllProposal },
                setState = { newState -> state.update { it.copy(getAllProposal = newState) } },
            )
    }


    private fun switchItineraries() {
        val currentUserId = state.value.getCurrentUserId ?: return
        state.update {
            it.copy(
                filterOptions = it.filterOptions.copy(
                    selectedProfileIds = if (state.value.filterOptions.selectedProfileIds.isEmpty())
                        setOf(currentUserId) else
                        emptySet(),
                ),
            )
        }

        getAllItinerary()
    }


    private fun switchProposals() {
        state.update {
            it.copy(assigned = !it.assigned)
        }

        getAllProposal()
    }

    private fun getAllItinerary() =
        getData(
            getData = { itineraryRepository.getAllItineraryToConfirm(state.value.filterOptions) },
            getState = { state.value.getAllItinerary },
            setState = { newState -> state.update { it.copy(getAllItinerary = newState) } },
        )

    private fun getAllProfile() =
        getData(
            getData = { profileRepository.getAll() },
            getState = { state.value.getAllProfile },
            setState = { newState -> state.update { it.copy(getAllProfile = newState) } },
        )

    private val getNextTaskPageJobList = mutableListOf<Job>()
    private fun getNextTaskPage() {
        val profileId = state.value.getCurrentUserId

        if (profileId.isNullOrEmpty())
            return

        getPaginationData(
            getData = {
                val nextPage = state.value.getAllTask.loadedPages + 1L
                taskRepository.getAll(
                    page = nextPage,
                    limit = 5,
                    filterOptions = TaskFilterOptions(
                        selectedProfileIds = setOf(profileId),
                        selectedTaskStatus = setOf(TaskModel.Status.Todo.toString()),
                    ),
                )
            },
            getState = { state.value.getAllTask },
            setState = { newState ->
                state.update {
                    it.copy(
                        getAllTask = newState,
                    )
                }
            },
            onSuccess = { _, tasks ->
                val categoryIds = tasks
                    .mapNotNull { it.itineraryService?.service?.category }
                    .filter { id -> state.value.getAllCategory.data.orEmpty().none { id == it.id } }

                if (categoryIds.isNotEmpty())
                    getCategories(categoryIds.toSet())
            },
        ).also { job ->
            try {
                getNextTaskPageJobList.add(job)
                job.invokeOnCompletion {
                    getNextTaskPageJobList.removeAll { it.isCompleted }
                }
            } catch (e: Exception) {
                e.printStackTrace()
            }
        }
    }


    private fun getCategories(ids: Set<String>) =
        getData(
            getData = { categoryRepository.getByIds(ids.toList()) },
            getState = { state.value.getAllCategory },
            setState = { newState ->
                if (!newState.isSuccess)
                    state.update {
                        it.copy(
                            getAllCategory = newState,
                        )
                    }
            },
            onSuccess = { newCategories ->
                state.update {
                    it.copy(
                        getAllCategory = State.success(it.getAllCategory.data.orEmpty() + newCategories),
                    )
                }
            },
        )

    private fun updateTask(event: DashboardEvent.UpdateTask) {
        val taskList = state.value.getAllTask.data
            .update(
                predicate = { it.id == event.task.id },
                update = { event.task },
            )

        state.value = state.value.copy(
            getAllTask = state.value.getAllTask.copy(
                data = taskList,
            ),
        )
    }

    private fun updateTaskStatus(event: DashboardEvent.UpdateTaskStatus) =
        scope.launch {
            val task = state.value.getAllTask.data
                .find { it.id == event.taskId }
                ?.copy(status = event.status) ?: return@launch

            updateTaskUseCase.update(task = task)
        }
}